import React, { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import AuthCard from '../components/AuthCard';
import { getAuthService } from '../services';
import { validatePassword } from '../services/validation';
import { useAuth } from '../auth/AuthContext';

interface SetNewPasswordScreenProps {
    email: string;
    /** Email MFA still has to be completed after the new password. */
    onMfaRequired: () => void;
    onBack: () => void;
}

/**
 * First-login password change for an invited (approver-created) user. Cognito
 * put the account in FORCE_CHANGE_PASSWORD; the user signed in with the
 * temporary password and must now choose a permanent one. On this pool, email
 * MFA follows, so success may route to the MFA step rather than straight in.
 */
export default function SetNewPasswordScreen({
    email,
    onMfaRequired,
    onBack,
}: SetNewPasswordScreenProps) {
    const { setUser } = useAuth();
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        setError(null);
        const pwError = validatePassword(password);
        if (pwError) {
            setError(pwError);
            return;
        }
        if (password !== confirm) {
            setError('The passwords do not match.');
            return;
        }
        setSubmitting(true);
        try {
            const result = await getAuthService().completeNewPassword(password, email);
            if (result.status === 'EMAIL_MFA_REQUIRED') {
                onMfaRequired();
            } else if (result.status === 'SIGNED_IN') {
                setUser(result.user);
            } else {
                setError('Could not complete sign-in. Please try again.');
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not set your password.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AuthCard title="Set your password" subtitle={`Finish setting up ${email}.`}>
            <SpaceBetween size="l">
                <FormField
                    label="New password"
                    description="At least 8 characters with upper, lower, and a number."
                >
                    <Input
                        type="password"
                        value={password}
                        onChange={({ detail }) => setPassword(detail.value)}
                    />
                </FormField>
                <FormField label="Confirm password">
                    <Input
                        type="password"
                        value={confirm}
                        onChange={({ detail }) => setConfirm(detail.value)}
                    />
                </FormField>
                {error ? <Alert type="error">{error}</Alert> : null}
                <Button variant="primary" loading={submitting} onClick={handleSubmit}>
                    Set password &amp; continue
                </Button>
                <Button variant="link" onClick={onBack}>
                    Back to sign in
                </Button>
            </SpaceBetween>
        </AuthCard>
    );
}
